<script lang="ts">
  import { user } from '../lib/stores/auth';
  import { api, ApiError } from '../lib/api';
  import { showToast } from '../lib/stores/toast';
  import { MS_PER_DAY } from '../lib/constants';

  // Sticky top-of-page banner shown to signed-in users whose email isn't
  // verified yet. Lives outside the route shells so it persists across
  // navigation; both PageShell and AppLayout offset their top edge by
  // var(--verify-banner-h) so the bar doesn't overlap their chrome.
  //
  // Two states (per auth-plan §9.2):
  //   day 0–6: gentle "verify to unlock donations + leaderboards"
  //   day 7+:  stronger "donations + leaderboards are paused"
  //
  // Dismissal is per-tab (sessionStorage), not per-account — the spec
  // wants the bar back on reload so a returning user sees the nudge.

  const SESSION_DISMISS_KEY = 'atlasguess.verify-banner.dismissed';
  const BANNER_HEIGHT_PX = 44;

  let dismissed = $state(loadDismissed());
  let resending = $state(false);

  function loadDismissed(): boolean {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
  }

  // Compute days since signup. created_at is an ISO string (UserResource).
  // Anything that fails to parse falls back to 0 days so the gentler copy
  // shows — better to under-warn than to scare the user with a stale clock.
  let daysSinceSignup = $derived.by(() => {
    const iso = $user?.created_at;
    if (!iso) return 0;
    const created = Date.parse(iso);
    if (Number.isNaN(created)) return 0;
    return Math.floor((Date.now() - created) / MS_PER_DAY);
  });

  let visible = $derived(Boolean($user) && !$user?.email_verified_at && !dismissed);

  let urgent = $derived(daysSinceSignup >= 7);

  // Drive the layout offset via a CSS custom property on :root. Both shells
  // already reference it (see app-layout.scss / page-shell.scss). Cleared
  // on hide so verified users / dismissals don't keep the gutter.
  $effect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (visible) {
      root.style.setProperty('--verify-banner-h', `${BANNER_HEIGHT_PX}px`);
    } else {
      root.style.removeProperty('--verify-banner-h');
    }
    return () => {
      root.style.removeProperty('--verify-banner-h');
    };
  });

  async function resend(): Promise<void> {
    if (resending) return;
    resending = true;
    try {
      await api('/api/auth/email/verify/resend', { method: 'POST' });
      showToast('Verification email sent. Check your inbox.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        showToast('Too many requests. Wait a bit and try again.', 'warn');
      } else {
        showToast("Couldn't send the email. Please try again.", 'error');
      }
    } finally {
      resending = false;
    }
  }

  function dismiss(): void {
    dismissed = true;
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    }
  }
</script>

{#if visible}
  <div
    class="verify-banner"
    class:verify-banner--urgent={urgent}
    role="region"
    aria-label="Email verification reminder"
  >
    <div class="verify-banner__inner">
      <p class="verify-banner__message">
        {#if urgent}
          <strong>Your email isn't verified.</strong>
          Donations and leaderboards are paused until you verify.
        {:else}
          <strong>Please verify your email</strong>
          to unlock donations and the daily leaderboard.
        {/if}
      </p>

      <div class="verify-banner__actions">
        <button
          type="button"
          class="verify-banner__resend"
          onclick={resend}
          disabled={resending}
        >
          {resending ? 'Sending…' : 'Resend email'}
        </button>
        <button
          type="button"
          class="verify-banner__dismiss"
          onclick={dismiss}
          aria-label="Dismiss for this session"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>
  </div>
{/if}
